import { Calendar, Award, Building2, BookOpen, Users, Landmark, ArrowRight, Sparkles, GraduationCap, Globe, TrendingUp } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Helmet } from "react-helmet-async"

export default function Historique() {
    const milestones = [
        {
            year: "1999",
            era: "Les Débuts",
            title: "La Naissance d'un Rêve",
            description: "Le 12 octobre 1999, par l'Ordonnance Ministérielle n° 610/1865, naît l'Université de Ngozi. Première université privée agréée de l'intérieur du pays, elle répond à un besoin crucial : décentraliser l'enseignement supérieur et offrir aux jeunes du nord du Burundi un accès à une éducation de qualité.",
            icon: Building2,
            quote: "Apporter une éducation de qualité au cœur de la région nord du Burundi.",
            achievements: [
                "Première université privée de l'intérieur",
                "Création officielle par ordonnance ministérielle",
                "Ouverture des premières facultés"
            ]
        },
        {
            year: "2005",
            era: "L'Expansion",
            title: "Croissance et Diversification",
            description: "Six ans après sa création, l'université franchit une étape majeure avec l'ouverture de nouvelles facultés. La Faculté de Médecine et la Faculté d'Agronomie voient le jour, marquant l'engagement de l'institution dans la formation de professionnels de santé et d'agronomes pour soutenir le développement local et national.",
            icon: BookOpen,
            quote: "Former les professionnels dont le pays a besoin.",
            achievements: [
                "Ouverture de la Faculté de Médecine",
                "Création de la Faculté d'Agronomie",
                "Expansion du corps professoral"
            ]
        },
        {
            year: "2012",
            era: "La Modernisation",
            title: "Harmonisation Internationale",
            description: "L'Université de Ngozi adopte le système Licence-Master-Doctorat (LMD) conformément aux directives de la Communauté Est-Africaine (EAC). Cette réforme majeure facilite la mobilité des étudiants et assure la reconnaissance internationale des diplômes, ouvrant ainsi de nouvelles perspectives pour les diplômés.",
            icon: Award,
            quote: "S'aligner sur les standards internationaux d'excellence.",
            achievements: [
                "Adoption du système LMD",
                "Accréditation EAC",
                "Reconnaissance internationale des diplômes"
            ]
        },
        {
            year: "2024",
            era: "L'Excellence",
            title: "Un Quart de Siècle d'Impact",
            description: "Aujourd'hui, l'Université de Ngozi est une institution florissante qui compte environ 3,000 étudiants actifs et a formé plus de 8,000 alumni. Avec 5 facultés et 5 instituts supérieurs professionnels, et environ 30 parcours de formation, elle continue d'écrire son histoire.",
            icon: GraduationCap,
            quote: "Former les leaders de demain, aujourd'hui.",
            achievements: [
                "Environ 3,000 étudiants actifs",
                "Plus de 8,000 diplômés à travers le monde",
                "5 Facultés et 5 Instituts supérieurs professionnels",
                "10+ partenariats internationaux"
            ]
        }
    ]

    return (
        <div className="min-h-screen bg-background font-sans text-foreground selection:bg-accent selection:text-accent-foreground">
            <Helmet>
                <title>Notre Historique - Université de Ngozi</title>
                <meta name="description" content="Découvrez l'histoire de l'Université de Ngozi depuis sa fondation en 1999. Un voyage à travers 25 ans d'excellence académique." />
            </Helmet>

            {/* Hero Section - Standard (ref: DroitEcoGestion) */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Notre Histoire</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Depuis 1999</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">Un Quart de Siècle</span>
                            <span className="block text-white mt-2">d'Excellence</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Depuis sa fondation en 1999, l'Université de Ngozi a tracé un chemin d'excellence et d'innovation dans le paysage éducatif du Burundi.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl">
                                Découvrir Notre Vision
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Stats Bar */}
            <section className="bg-secondary/5 border-b border-border relative overflow-hidden">
                <div className="container py-8 flex flex-wrap items-center justify-between gap-12">
                    {[
                        { val: "1999", label: "Année de Fondation", icon: Award },
                        { val: "25+", label: "Années d'Excellence", icon: TrendingUp },
                        { val: "3,000+", label: "Étudiants Actifs", icon: Users },
                        { val: "8,000+", label: "Alumni", icon: GraduationCap }
                    ].map((stat, i) => (
                        <div key={i} className="flex items-center gap-4 group">
                            <div className="p-3 bg-primary/5 rounded-xl group-hover:bg-accent/10 transition-colors">
                                <stat.icon className="w-6 h-6 text-primary group-hover:text-accent transition-colors" />
                            </div>
                            <div>
                                <div className="text-2xl font-black text-primary leading-none mb-1">{stat.val}</div>
                                <div className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{stat.label}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Main Timeline Section */}
            <article className="container section-padding">
                <div className="grid lg:grid-cols-[280px_1fr] gap-16 lg:gap-24">
                    {/* Floating Sidebar */}
                    <aside className="lg:sticky lg:top-32 lg:self-start hidden lg:block">
                        <div className="space-y-12">
                            <nav className="space-y-2">
                                <p className="text-[11px] uppercase tracking-[0.25em] font-black text-muted-foreground mb-8 pb-4 border-b-2 border-primary/10">
                                    Chronologie
                                </p>
                                {milestones.map((item, i) => (
                                    <a
                                        key={i}
                                        href={`#${item.year}`}
                                        className="group flex items-center justify-between py-3 text-xs font-black text-muted-foreground hover:text-primary transition-all pr-4 border-r-2 border-transparent hover:border-primary"
                                    >
                                        <div className="flex items-center gap-4">
                                            <span className="text-[10px] font-serif italic text-accent opacity-0 group-hover:opacity-100 transition-opacity">{item.year}</span>
                                            <span className="uppercase tracking-widest">{item.era}</span>
                                        </div>
                                        <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                                    </a>
                                ))}
                            </nav>

                            <div className="bg-primary p-8 rounded-2xl text-primary-foreground relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-700" />
                                <h4 className="text-[10px] uppercase tracking-[0.2em] font-black mb-6 text-accent">Moments Clés</h4>
                                <p className="text-xs font-light leading-relaxed mb-6 opacity-80">
                                    Chaque étape a façonné l'institution que nous sommes aujourd'hui.
                                </p>
                            </div>
                        </div>
                    </aside>

                    {/* Timeline Content */}
                    <main className="space-y-24">
                        {/* Section Title */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-1 bg-accent rounded-full" />
                                <span className="text-xs font-black uppercase tracking-[0.3em] text-primary">Le Fil de Notre Histoire</span>
                            </div>
                            <h2 className="heading-2 font-serif italic">Chaque étape raconte notre engagement.</h2>
                        </div>

                        {/* Timeline Items */}
                        {milestones.map((milestone, idx) => (
                            <section key={idx} id={milestone.year} className="scroll-mt-32 relative">
                                {/* Connecting Line for Desktop */}
                                {idx < milestones.length - 1 && (
                                    <div className="hidden lg:block absolute left-0 top-32 bottom-0 w-px bg-gradient-to-b from-accent via-border to-transparent -ml-12" />
                                )}

                                <div className="grid lg:grid-cols-[200px_1fr] gap-12 items-start">
                                    {/* Year Display */}
                                    <div className="relative">
                                        <div className="lg:sticky lg:top-32">
                                            {/* Large Year */}
                                            <div className="relative mb-6">
                                                <div className="text-8xl lg:text-9xl font-black text-primary/5 leading-none">
                                                    {milestone.year}
                                                </div>
                                                <div className="absolute inset-0 flex items-center">
                                                    <span className="text-4xl lg:text-5xl font-black text-primary">{milestone.year}</span>
                                                </div>
                                            </div>

                                            {/* Era Badge */}
                                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full border border-accent/20">
                                                <div className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                                                <span className="text-xs font-black uppercase tracking-widest text-accent">{milestone.era}</span>
                                            </div>

                                            {/* Icon */}
                                            <div className="hidden lg:block mt-8 w-14 h-14 bg-primary/5 rounded-2xl flex items-center justify-center">
                                                <milestone.icon className="w-7 h-7 text-primary" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="space-y-8">
                                        {/* Title */}
                                        <div className="space-y-4">
                                            <h3 className="heading-3 font-serif italic text-foreground">
                                                {milestone.title}
                                            </h3>
                                        </div>

                                        {/* Description */}
                                        <div className="prose-base text-muted-foreground/90 font-light leading-relaxed">
                                            <p>{milestone.description}</p>
                                        </div>

                                        {/* Quote */}
                                        <div className="border-l-4 border-accent/30 pl-8 py-4 bg-accent/5">
                                            <p className="text-sm italic text-foreground font-medium">
                                                "{milestone.quote}"
                                            </p>
                                        </div>

                                        {/* Achievements */}
                                        <div className="space-y-4">
                                            <p className="text-xs font-black uppercase tracking-widest text-muted-foreground">Réalisations Clés</p>
                                            <ul className="space-y-3">
                                                {milestone.achievements.map((achievement, i) => (
                                                    <li key={i} className="flex items-start gap-4">
                                                        <div className="w-px h-6 bg-accent mt-1" />
                                                        <span className="text-sm text-muted-foreground font-light">{achievement}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Divider */}
                                        {idx < milestones.length - 1 && (
                                            <div className="pt-12 mt-12 border-t border-border" />
                                        )}
                                    </div>
                                </div>
                            </section>
                        ))}
                    </main>
                </div>
            </article>

            {/* Impact Stats Section */}
            <section className="bg-gradient-to-br from-primary/5 via-accent/5 to-primary/5 border-y border-border">
                <div className="container section-padding">
                    <div className="text-center mb-16">
                        <div className="flex items-center gap-3 justify-center mb-4">
                            <Calendar className="w-5 h-5 text-accent" />
                            <span className="text-xs font-black uppercase tracking-widest text-accent">Notre Impact</span>
                        </div>
                        <h2 className="heading-2 font-serif">25 Ans de Contributions</h2>
                        <p className="text-lg text-muted-foreground mt-4">
                            au développement du Burundi
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 max-w-5xl mx-auto">
                        {[
                            { value: "8,000+", label: "Diplômés", icon: GraduationCap },
                            { value: "3,000+", label: "Étudiants Actifs", icon: Users },
                            { value: "10", label: "5 Facultés & 5 Instituts supérieurs professionnels", icon: Building2 },
                            { value: "10+", label: "Partenariats Internationaux", icon: Globe }
                        ].map((stat, i) => (
                            <div key={i} className="text-center space-y-6">
                                <div className="w-16 h-16 mx-auto bg-primary/5 rounded-2xl flex items-center justify-center">
                                    <stat.icon className="w-8 h-8 text-primary" />
                                </div>
                                <div>
                                    <div className="text-4xl lg:text-5xl font-black text-primary mb-2">{stat.value}</div>
                                    <div className="text-sm font-bold text-muted-foreground uppercase tracking-wider">{stat.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Footer */}
            <footer className="bg-primary text-white border-t-8 border-accent relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-accent/5 -skew-x-12 origin-top-right pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-px h-full bg-white/10 ml-[20%]" />

                <div className="container py-24 text-center relative z-10">
                    <div className="max-w-2xl mx-auto space-y-10">
                        <div className="inline-flex items-center gap-6 mb-4">
                            <div className="h-px w-8 bg-accent" />
                            <span className="text-[11px] uppercase tracking-[0.4em] text-accent font-black">Rejoignez-Nous</span>
                            <div className="h-px w-8 bg-accent" />
                        </div>

                        <h2 className="heading-1 leading-tight font-serif italic text-white">
                            Faites Partie de <br /><span className="text-accent">Notre Histoire</span>.
                        </h2>

                        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                            <Button className="bg-accent text-accent-foreground px-12 py-8 text-sm font-black hover:bg-white hover:text-primary hover:-translate-y-2 transition-all duration-500 rounded-2xl transform">
                                Découvrir Nos Programmes
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                            <Button variant="ghost" className="text-white border-2 border-white/20 hover:bg-white/5 px-12 py-8 text-sm font-black rounded-2xl transition-all">
                                Nous Contacter
                            </Button>
                        </div>

                        <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-medium mt-12">
                            © 2024 Université de Ngozi · Notre Histoire
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    )
}
